import { apply, burst, celebrate, onceToday, toast } from '../core/effects';
import { hasGame, S } from '../core/game';
import { m } from '../i18n';
import { objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { openMinigame } from '../minigames/registry';
import { setFestivalSource } from '../world/map';
import type { ObjectId } from '../world/mapdef';
import { stamp } from './passport';

/** A festival every game week from week 2: lanterns, dragon boats, the moon, the new year — then a quiet week. */
export type FestivalId = 'lantern' | 'dragonBoat' | 'moon' | 'newYear';
const CYCLE: readonly (FestivalId | null)[] = ['lantern', 'dragonBoat', 'moon', 'newYear', null];
const STAMP: Record<FestivalId, string> = { lantern: 'lantern', dragonBoat: 'dragonBoat', moon: 'moon', newYear: 'newYear' };

export function festival(): FestivalId | null {
  if (!hasGame()) return null;
  const w = S().week;
  return w < 2 ? null : CYCLE[(w - 2) % CYCLE.length];
}
setFestivalSource(festival);

/** Day-start announcement (called by the day cycle). */
export function announceFestival(): void {
  const f = festival();
  if (f) toast(m().world3.fest.announce(m().world3.fest.names[f]));
}

const shuffle = <T>(a: T[]): T[] => a.map((v) => [Math.random(), v] as const).sort((x, y) => x[0] - y[0]).map((p) => p[1]);

function celebrated(f: FestivalId): void {
  stamp(STAMP[f]);
}

/** Lantern riddles: three a day, picked by the day number. */
function riddles(): void {
  const t = m().world3.fest.riddles;
  const s = S();
  const day = s.week * 3 + s.dayIndex;
  const todays = [0, 1, 2].map((k) => (day * 3 + k) % t.list.length);
  const open = todays.filter((i) => !s.daily[`riddle.${i}`]);
  if (!open.length) return say({ who: objName('fsRiddles'), icon: '🏮', text: t.allDone });
  const i = open[0];
  const r = t.list[i];
  sayEnglish(r.q, 0.85);
  say({
    who: objName('fsRiddles'),
    icon: '🏮',
    text: `${t.intro}\n\n“${r.q}”`,
    choices: [
      ...shuffle(r.a.map((a, k) => ({ a, ok: k === 0 }))).map(
        (o): Choice => ({
          text: o.a,
          run: () => {
            sayEnglish(o.a, 0.9);
            if (!o.ok) {
              sfx('bad');
              return say({ who: objName('fsRiddles'), icon: '🏮', text: t.wrong, choices: [{ text: m().common.back, run: riddles }] });
            }
            s.daily[`riddle.${i}`] = true;
            sfx('ok');
            burst('sparkle', 8);
            apply({ stars: 2, study: 1 });
            celebrated('lantern');
            say({ who: objName('fsRiddles'), icon: '🎉', text: t.right, choices: open.length > 1 ? [{ text: t.guess, run: riddles }, { text: m().common.ok }] : undefined });
          },
        }),
      ),
      { text: m().common.back },
    ],
  });
}

/** Zongzi: put the wrapping steps in order. */
function zongzi(step = 0): void {
  const t = m().world3.fest;
  const st = t.zongziSteps[step];
  if (!st) {
    celebrate();
    apply({ energy: 15, mood: 10, stars: 4, minutes: 30 });
    celebrated('dragonBoat');
    return say({ who: objName('fsZongzi'), icon: '🍙', text: t.zongziDone });
  }
  say({
    who: objName('fsZongzi'),
    icon: '🍙',
    text: step === 0 ? `${t.zongzi}\n\n${st.q}` : st.q,
    choices: shuffle(st.a.map((a, k) => ({ a, ok: k === 0 }))).map((o) => ({
      text: o.a,
      run: () => {
        if (o.ok) {
          sfx('pop');
          zongzi(step + 1);
        } else {
          sfx('bad');
          say({ who: objName('fsZongzi'), icon: '🍙', text: t.zongziWrong, choices: [{ text: m().common.back, run: () => zongzi(step) }] });
        }
      },
    })),
  });
}

/** A simple "do it once a day" festival activity. */
function activity(id: ObjectId, icon: string, text: string, label: string, done: string, fx: Parameters<typeof apply>[0], f: FestivalId, english?: string): void {
  say({
    who: objName(id),
    icon,
    text,
    choices: [
      {
        text: label,
        run: () => {
          if (!onceToday(`fest.${id}`)) return toast(m().common.alreadyToday);
          sfx('fanfare');
          burst('heart', 6);
          apply(fx);
          celebrated(f);
          if (english) sayEnglish(english, 0.9);
          say({ who: objName(id), icon, text: done });
        },
      },
      { text: m().common.back },
    ],
  });
}

export const FEST_OBJECTS: Partial<Record<ObjectId, () => void>> = {
  fsRiddles: riddles,
  fsTangyuan() {
    const t = m().world3.fest;
    activity('fsTangyuan', '🥣', t.tangyuan, t.eatTangyuan, t.tangyuanDone, { energy: 15, mood: 10, coins: -10, minutes: 15 }, 'lantern');
  },
  fsBoat() {
    const t = m().world3.fest;
    say({
      who: objName('fsBoat'),
      icon: '🐉',
      text: t.boat,
      choices: [{ text: t.race, run: () => (celebrated('dragonBoat'), openMinigame('dragonBoat' as Parameters<typeof openMinigame>[0])) }, { text: m().common.back }],
    });
  },
  fsZongzi: () => zongzi(0),
  fsEgg() {
    const t = m().world3.fest;
    say({
      who: objName('fsEgg'),
      icon: '🥚',
      text: t.egg,
      choices: [
        {
          text: t.tryEgg,
          run: () => {
            const time = S().time;
            if (time < 11 * 60 || time >= 13 * 60) return toast(t.eggNoon);
            if (Math.random() < 0.45) {
              sfx('fanfare');
              celebrate(true);
              if (onceToday('fest.egg')) apply({ stars: 5, mood: 10 });
              celebrated('dragonBoat');
              say({ who: objName('fsEgg'), icon: '🥚', text: t.eggWin });
            } else {
              sfx('bad');
              say({ who: objName('fsEgg'), icon: '🥚', text: t.eggFail, choices: [{ text: t.tryEgg, run: () => FEST_OBJECTS.fsEgg!() }, { text: m().common.back }] });
            }
          },
        },
        { text: m().common.back },
      ],
    });
  },
  fsBBQ() {
    const t = m().world3.fest;
    activity('fsBBQ', '🌽', t.bbq, t.joinBBQ, t.bbqDone, { energy: 20, mood: 15, minutes: 40 }, 'moon', 'Happy Moon Festival!');
  },
  fsPomelo() {
    const t = m().world3.fest;
    activity('fsPomelo', '🍈', t.pomelo, t.pomeloHat, t.pomeloDone, { mood: 15, energy: 5 }, 'moon');
  },
  fsLion() {
    const t = m().world3.fest;
    activity('fsLion', '🦁', t.lion, t.watchLion, t.lionDone, { mood: 18, stars: 3, minutes: 20 }, 'newYear');
  },
  fsReunion() {
    const t = m().world3.fest;
    const s = S();
    const key = `newYear.${s.week}`;
    if (s.forever[key]) return say({ who: objName('fsReunion'), icon: '🍲', text: t.reunionAgain });
    say({
      who: objName('fsReunion'),
      icon: '🍲',
      text: t.reunion,
      choices: [
        {
          text: t.eatReunion,
          run: () => {
            s.forever[key] = true;
            sfx('fanfare');
            celebrate(true);
            apply({ energy: 30, mood: 20, coins: 88, minutes: 60 });
            celebrated('newYear');
            sayEnglish('Happy New Year! Thank you!', 0.9);
            say({ who: objName('fsReunion'), icon: '🧧', text: t.reunionDone });
          },
        },
        { text: m().common.back },
      ],
    });
  },
};
